(() => {
  "use strict";

  const ROWS_HOLDER = "[data-timeline-rows]";
  const ROW = "[data-timeline-row]";
  const ROW_EDITOR = "[data-timeline-row-editor]";
  const BUTTON = "[data-timeline-button]";
  const BLOCK_SCROLL = "[data-block-scroll]";
  const SELECTED = "is-selected";

  class TimelineComponent extends HTMLElement {
    constructor() {
      super();
      this.rows = this.querySelectorAll(ROW);
      this.rowsHolder = this.querySelector(ROWS_HOLDER);
      this.buttons = this.querySelectorAll(BUTTON);
      this.rowsWidth = 0;
      this.rowsHeight = [];
      this.holderTop = 0;
      this.holderHeight = 0;
      this.requestAnimation = null;
      this.isDesktopView = !window.theme.isMobile();
      this.isScrollEnabled = false;
      this.scrollEvent = (event) => this.scrollEvents(event);
      this.resizeEvent = () => this.resizeEvents();
    }

    connectedCallback() {
      if (!this.rowsHolder || this.rows.length < 2) {
        return;
      }

      this.holderTop = this.getBoundingClientRect().top + window.scrollY;
      this.holderHeight = this.offsetHeight;
      this.calculateRowsDimensions();
      this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

      if (this.isDesktopView && !this.isScrollEnabled) {
        this.isScrollEnabled = true;
        document.addEventListener("theme:scroll", this.scrollEvent);
      }

      this.rowsHolder.addEventListener("scroll", this.scrollEvent);
      document.addEventListener("theme:resize:width", this.resizeEvent);

      if (this.buttons.length) {
        this.buttons.forEach((button) => {
          button.addEventListener("click", (event) => {
            event.preventDefault();
            const row = event.currentTarget.closest(ROW);
            if (!row) {
              return;
            }

            if (window.theme.isMobile()) {
              const offset = 16;
              this.rowsHolder.scrollTo({
                left: row.offsetLeft - offset,
                behavior: "smooth",
              });
              return;
            }

            const anchor = this.getRowAnchor(row);
            if (!anchor) {
              return;
            }
            const rectTop = anchor.getBoundingClientRect().top;
            const triggerOffset = window.innerHeight * 0.33;
            window.theme.scrollTo(rectTop - triggerOffset + 1);
          });
        });
      }
    }

    resizeEvents() {
      this.holderTop = this.getBoundingClientRect().top + window.scrollY;
      this.holderHeight = this.offsetHeight;
      this.calculateRowsDimensions();
      this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

      const isDesktop = !window.theme.isMobile();
      if (isDesktop && !this.isScrollEnabled) {
        this.isScrollEnabled = true;
        document.addEventListener("theme:scroll", this.scrollEvent);
      } else if (!isDesktop && this.isScrollEnabled) {
        this.isScrollEnabled = false;
        document.removeEventListener("theme:scroll", this.scrollEvent);
      }
    }

    scrollEvents(event) {
      if (!this.requestAnimation) {
        const currentTarget = event.currentTarget;
        this.requestAnimation = requestAnimationFrame(() =>
          this.calculatePosition(currentTarget)
        );
      }
    }

    removeAnimationFrame() {
      if (this.requestAnimation) {
        cancelAnimationFrame(this.requestAnimation);
        this.requestAnimation = null;
      }
    }

    calculateRowsDimensions() {
      if (!this.rows.length) {
        return;
      }

      this.rowsHeight = [];
      this.rowsWidth = 0;

      let totalHeight = 0;
      this.rows.forEach((row, index) => {
        this.rowsHeight.push(row.offsetHeight);
        this.rowsWidth +=
          row.offsetWidth + parseInt(getComputedStyle(row).marginRight, 10);

        if (window.Shopify.designMode) {
          const editors = this.querySelectorAll(ROW_EDITOR);
          if (editors.length) {
            totalHeight += row.offsetHeight;
            editors[index]?.style.setProperty(
              "--row-height-min",
              `${row.offsetHeight}px`
            );
            editors[index + 1]?.style.setProperty(
              "--row-top-mobile",
              `${totalHeight}px`
            );
          }
        }
      });
    }

    getRowAnchor(row) {
      if (!row) {
        return null;
      }
      const anchor =
        row.querySelector("[data-timeline-anchor]") ||
        row.querySelector(".timeline__content__inner") ||
        row;
      return anchor.firstElementChild || anchor;
    }

    calculatePosition(holder = null) {
      this.removeAnimationFrame();
      this.holderTop = this.getBoundingClientRect().top + window.scrollY;

      const isDesktop = !window.theme.isMobile();
      const viewportHeight = window.innerHeight;
      const headerOffset = document.querySelector("[data-header-sticky]")
        ? document.querySelector("[data-header-height]")?.offsetHeight || 0
        : 0;
      const triggerY = headerOffset + viewportHeight * 0.33;
      const triggerDoc = window.scrollY + triggerY;

      let percentMobile = 0;
      if (!isDesktop && holder) {
        const scrollLeft = holder.scrollLeft - 16;
        const holderWidth = holder.offsetWidth;
        percentMobile = (scrollLeft / (this.rowsWidth - holderWidth)) * 100;
        this.style.setProperty("--percent-mobile", `${percentMobile}%`);
      }

      if (
        isDesktop &&
        (triggerDoc < this.holderTop ||
          triggerDoc > this.holderTop + this.holderHeight + viewportHeight)
      ) {
        return;
      }

      if (isDesktop) {
        const anchorTops = [];

        this.rows.forEach((row, index) => {
          const anchor = this.getRowAnchor(row) || row;
          anchorTops[index] = anchor.getBoundingClientRect().top;
        });

        let activeIndex = 0;
        this.rows.forEach((row, index) => {
          if (anchorTops[index] <= triggerY) {
            activeIndex = index;
          }
        });

        this.rows.forEach((row, index) => {
          if (index > 0) {
            const prevTop = anchorTops[index - 1];
            const currTop = anchorTops[index];
            const span = currTop - prevTop;
            const progress =
              span <= 0
                ? 0
                : Math.min(
                    Math.max(((triggerY - prevTop) / span) * 100, 0),
                    100
                  );
            row.previousElementSibling?.style.setProperty(
              "--percent-desktop",
              `${progress}%`
            );
          }
          row.classList.toggle(SELECTED, index <= activeIndex);
        });
        return;
      }

      this.rows.forEach((row, index) => {
        if (index > 0) {
          const threshold = (row.offsetLeft / this.rowsWidth) * 100;
          row.classList.toggle(SELECTED, percentMobile > threshold);
        }
      });
    }

    disconnectedCallback() {
      if (this.isScrollEnabled) {
        this.isScrollEnabled = false;
        document.removeEventListener("theme:scroll", this.scrollEvent);
      }

      if (this.rowsHolder) {
        this.rowsHolder.removeEventListener("scroll", this.scrollEvent);
      }

      document.removeEventListener("theme:resize:width", this.resizeEvent);
    }

    onBlockSelect(event) {
      const holder = this.querySelector(BLOCK_SCROLL);
      if (!holder) {
        return;
      }

      const block = event.srcElement;
      const index = [...block.parentElement.children].indexOf(block);
      const row = this.rows[index];
      if (row) {
        holder.scrollTo({ top: 0, left: row.offsetLeft, behavior: "smooth" });
      }
    }
  }

  if (!customElements.get("timeline-component")) {
    customElements.define("timeline-component", TimelineComponent);
  }
})();
